'use client'

import { useEffect, useState } from 'react'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { BookOpen, Search } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ui/web/components'
import { ClassroomIcon, CollectionIcon, QuizIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { FavoritePageProps, FavoriteTab } from './types'
import { BookDetailModal } from '../../components/book-detail-modal'
import { BookCollectionCard, BookCoverCard, LessonModuleCard } from '../../components/cards'
import { LessonModuleModal } from '../../components/lesson-module-modal'
import { useSharedTRPC } from '../../context'
import { ResultGroup } from './favorite-grids'
import { mapToLessonModuleModalData } from './map-lesson-module'
import { FAVORITE_TABS } from './types'

const TAB_META: Record<FavoriteTab, { Icon: typeof BookOpen; heading: string }> = {
  buku: { Icon: BookOpen, heading: 'Buku' },
  'koleksi-bacaan': { Icon: CollectionIcon, heading: 'Koleksi Bacaan' },
  'latihan-dan-kuis': { Icon: QuizIcon, heading: 'Latihan dan Kuis' },
  'proyek-dan-tugas': { Icon: ClassroomIcon, heading: 'Proyek dan Tugas' },
}

export function FavoritePage({
  tab,
  search,
  onTabChange,
  onSearchChange,
  renderBookModalAction,
  renderLessonModuleModalAction,
  renderCollectionAction,
  renderCollectionLink,
  renderLoveBookLink,
  renderExplorePage,
  renderSearchPage,
  className,
}: FavoritePageProps) {
  const trpc = useSharedTRPC()

  const [searchInput, setSearchInput] = useState(search)
  const [debouncedSearch] = useDebouncedValue(searchInput, { wait: 500 })
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null)
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null)

  // Re-sync the input when the controlling `?q=` param changes externally (back/forward nav).
  useEffect(() => {
    setSearchInput(search)
  }, [search])

  // Push the debounced value up to write `?q=`, only when it actually diverges from the
  // controlled prop — avoids spurious navigate() calls on every parent re-render.
  useEffect(() => {
    if (debouncedSearch !== search) onSearchChange(debouncedSearch)
  }, [debouncedSearch, search, onSearchChange])

  const booksQuery = useInfiniteQuery({
    ...trpc.loveBook.getAllLovedBooks.infiniteQueryOptions(
      { search: debouncedSearch, pageSize: 12 } as Parameters<
        typeof trpc.loveBook.getAllLovedBooks.infiniteQueryOptions
      >[0],
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
  })
  const collectionsQuery = useInfiniteQuery({
    ...trpc.loveBook.getAllLovedBookCollections.infiniteQueryOptions(
      { search: debouncedSearch, pageSize: 12 } as Parameters<
        typeof trpc.loveBook.getAllLovedBookCollections.infiniteQueryOptions
      >[0],
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
  })
  const latihanQuery = useInfiniteQuery({
    ...trpc.loveLessonModule.getAllLovedLessonModules.infiniteQueryOptions(
      { search: debouncedSearch, pageSize: 12, kind: 'practice' },
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
  })
  const proyekQuery = useInfiniteQuery({
    ...trpc.loveLessonModule.getAllLovedLessonModules.infiniteQueryOptions(
      { search: debouncedSearch, pageSize: 12, kind: 'project' },
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
  })

  const countOf = (q: { data?: { pages: { pagination: { total: number } }[] } }) =>
    q.data?.pages[0]?.pagination.total ?? 0

  const badge: Record<FavoriteTab, number> = {
    buku: countOf(booksQuery),
    'koleksi-bacaan': countOf(collectionsQuery),
    'latihan-dan-kuis': countOf(latihanQuery),
    'proyek-dan-tugas': countOf(proyekQuery),
  }

  const {
    data: bookDetails,
    isLoading: bookDetailsLoading,
    isError: bookDetailsError,
  } = useQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...trpc.library.getBookDetails.queryOptions({ id: selectedBookId! }),
    enabled: !!selectedBookId,
  })

  const {
    data: moduleDetails,
    isLoading: moduleDetailsLoading,
    isError: moduleDetailsError,
  } = useQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...trpc.lesson.getLessonModuleDetails.queryOptions({ lessonModuleId: selectedModuleId! }),
    enabled: !!selectedModuleId,
  })
  const mappedModuleData =
    moduleDetails?.type === 'lessonModule' ? mapToLessonModuleModalData(moduleDetails) : undefined

  const renderActive = () => {
    if (tab === 'buku') {
      const items = booksQuery.data?.pages.flatMap((p) => p.data) ?? []
      return (
        <ResultGroup
          isPending={booksQuery.isPending}
          fetchStatus={booksQuery.fetchStatus}
          isError={booksQuery.isError}
          isFetchingNextPage={booksQuery.isFetchingNextPage}
          hasNextPage={!!booksQuery.hasNextPage}
          items={items}
          variant='book'
          emptyKeyword={debouncedSearch || undefined}
          renderExplorePage={renderExplorePage}
          renderSearchPage={renderSearchPage}
          onRetry={() => void booksQuery.refetch()}
          onLoadMore={() => void booksQuery.fetchNextPage()}
        >
          <div className='srd:grid srd:grid-cols-2 srd:sm:grid-cols-3 srd:gap-4 srd:md:grid-cols-4 srd:lg:grid-cols-5 srd:xl:grid-cols-6 srd:2xl:grid-cols-8'>
            {items.map((item) => (
              <BookCoverCard
                key={item.book.id}
                id={item.book.id}
                coverUrl={item.book.coverUrl}
                action={{ type: 'callback', onClick: () => setSelectedBookId(item.book.id) }}
                className='srd:sm:w-full srd:aspect-3/4 srd:sm:h-auto srd:mx-auto'
              />
            ))}
          </div>
        </ResultGroup>
      )
    }

    if (tab === 'koleksi-bacaan') {
      const items = collectionsQuery.data?.pages.flatMap((p) => p.data) ?? []
      return (
        <ResultGroup
          isPending={collectionsQuery.isPending}
          fetchStatus={collectionsQuery.fetchStatus}
          isError={collectionsQuery.isError}
          isFetchingNextPage={collectionsQuery.isFetchingNextPage}
          hasNextPage={!!collectionsQuery.hasNextPage}
          items={items}
          variant='wide'
          emptyKeyword={debouncedSearch || undefined}
          renderExplorePage={renderExplorePage}
          renderSearchPage={renderSearchPage}
          onRetry={() => void collectionsQuery.refetch()}
          onLoadMore={() => void collectionsQuery.fetchNextPage()}
        >
          <div className='srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4 srd:md:grid-cols-3 srd:lg:grid-cols-4 srd:xl:grid-cols-5 srd:2xl:grid-cols-6'>
            {items.map((item) => (
              <BookCollectionCard
                key={item.bookCollection.id}
                id={item.bookCollection.id}
                coverUrl={item.bookCollection.coverUrl}
                action={renderCollectionAction({ id: item.bookCollection.id, slug: item.bookCollection.slug })}
                className='srd:sm:w-full srd:aspect-4/3 srd:sm:h-auto srd:mx-auto'
              />
            ))}
          </div>
        </ResultGroup>
      )
    }

    const isLatihan = tab === 'latihan-dan-kuis'
    const moduleQuery = isLatihan ? latihanQuery : proyekQuery
    const items = moduleQuery.data?.pages.flatMap((p) => p.data) ?? []
    return (
      <ResultGroup
        isPending={moduleQuery.isPending}
        fetchStatus={moduleQuery.fetchStatus}
        isError={moduleQuery.isError}
        isFetchingNextPage={moduleQuery.isFetchingNextPage}
        hasNextPage={!!moduleQuery.hasNextPage}
        items={items}
        variant='wide'
        emptyKeyword={debouncedSearch || undefined}
        renderExplorePage={renderExplorePage}
        renderSearchPage={renderSearchPage}
        onRetry={() => void moduleQuery.refetch()}
        onLoadMore={() => void moduleQuery.fetchNextPage()}
      >
        <div className='srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4 srd:md:grid-cols-3 srd:lg:grid-cols-4 srd:xl:grid-cols-5 srd:2xl:grid-cols-6'>
          {items.map((item) => (
            <LessonModuleCard
              key={item.module.id}
              id={item.module.id}
              coverUrl={item.module.coverUrl}
              action={{ type: 'callback', onClick: () => setSelectedModuleId(item.module.id) }}
              className='srd:sm:w-full srd:aspect-4/3 srd:sm:h-auto srd:mx-auto'
            />
          ))}
        </div>
      </ResultGroup>
    )
  }

  return (
    <main className={cn('srd:w-full', className)}>
      <Tabs value={tab} onValueChange={(value) => onTabChange(value as FavoriteTab)} className='srd:gap-y-8'>
        <div className='srd:flex srd:flex-col srd:gap-y-4'>
          <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-4'>
            <h2 className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-white')}>Simpan</h2>

            <div className='srd:w-full srd:flex-1 srd:min-w-[300px] srd:max-w-[480px]'>
              <InputGroup className='srd:rounded-full'>
                <InputGroupAddon align='inline-start'>
                  <Search />
                </InputGroupAddon>
                <InputGroupInput
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder='Cari berdasarkan judul...'
                />
              </InputGroup>
            </div>
          </div>

          <TabsList className='srd:flex srd:h-fit srd:w-fit srd:max-w-none srd:flex-wrap srd:justify-start srd:gap-2 srd:bg-transparent srd:p-0 ui:group-data-[orientation=horizontal]/tabs:h-fit'>
            {FAVORITE_TABS.map((id) => {
              const meta = TAB_META[id]
              return (
                <TabsTrigger
                  key={id}
                  value={id}
                  className='srd:h-auto srd:flex-none ui:group-data-[variant=default]/tabs-list:rounded-xl srd:border srd:border-blue-70 srd:px-4 srd:py-2 ui:group-data-[variant=default]/tabs-list:text-grey-80 srd:data-active:bg-blue-30 srd:data-active:border-blue-30 srd:data-active:text-navy-80'
                >
                  <span className='srd:inline-flex srd:items-center srd:gap-2'>
                    <meta.Icon color='currentColor' className='srd:shrink-0 srd:size-5' aria-hidden />
                    <span className={cn(typographyVariants({ variant: 'label-md' }))}>
                      {meta.heading}: {badge[id]}
                    </span>
                  </span>
                </TabsTrigger>
              )
            })}
          </TabsList>
        </div>

        {FAVORITE_TABS.map((id) => (
          <TabsContent key={id} value={id}>
            <AnimatePresence mode='wait'>
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
              >
                {id === tab ? renderActive() : null}
              </motion.div>
            </AnimatePresence>
          </TabsContent>
        ))}
      </Tabs>

      <BookDetailModal
        open={!!selectedBookId}
        onClose={() => setSelectedBookId(null)}
        book={bookDetails}
        isLoading={bookDetailsLoading && !!selectedBookId}
        isError={bookDetailsError}
        renderBookAction={renderBookModalAction}
        renderCollectionLink={renderCollectionLink}
        renderLoveBookLink={renderLoveBookLink}
        withShareCta
      />

      <LessonModuleModal
        open={!!selectedModuleId}
        onClose={() => setSelectedModuleId(null)}
        data={mappedModuleData}
        isLoading={moduleDetailsLoading && !!selectedModuleId}
        isError={moduleDetailsError}
        renderActivityAction={mappedModuleData ? renderLessonModuleModalAction(mappedModuleData) : undefined}
        showLoveButton
      />
    </main>
  )
}
